import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import type { RaceEvent, ExternalEvent, FavoriteGroupRide, FavoriteRace } from '../types';
import RaceEditorModal from '../components/AddRaceModal';
import CategorySelectionModal from '../components/CategorySelectionModal';
import RaceCard from '../components/RaceCard';
import ExternalEventCard from '../components/ExternalEventCard';
import { PlusIcon } from '../components/icons/PlaybackIcons';
import { raceEventService } from '../services/raceService';
import { eventIntegrationService } from '../services/eventIntegrationService';
import { userService } from '../services/userService';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';

const STORAGE_TAB_KEY = 'mobile_races_active_tab';
const STORAGE_FILTER_KEY = 'mobile_races_event_filter';
const STORAGE_SEARCH_KEY = 'mobile_races_search_term';
const STORAGE_SCROLL_KEY = 'mobile_races_scroll_y';

const MobileRacesPage: React.FC = () => {
    const { currentUser, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Active Tab: 'find' | 'my' (restored from searchParams or sessionStorage)
    const initialTab = (searchParams.get('tab') as 'find' | 'my') || 
                       (sessionStorage.getItem(STORAGE_TAB_KEY) as 'find' | 'my') || 
                       'find';
    const [activeTab, setActiveTab] = useState<'find' | 'my'>(initialTab);

    // Search & Filter State (restored from searchParams or sessionStorage)
    const initialFilter = (searchParams.get('filter') as 'all' | 'race' | 'group_ride') || 
                          (sessionStorage.getItem(STORAGE_FILTER_KEY) as 'all' | 'race' | 'group_ride') || 
                          'all';
    const [eventTypeFilter, setEventTypeFilter] = useState<'all' | 'race' | 'group_ride'>(initialFilter);

    const initialSearch = searchParams.get('q') || sessionStorage.getItem(STORAGE_SEARCH_KEY) || '';
    const [searchTerm, setSearchTerm] = useState(initialSearch);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch);

    // Data State
    const [allRaces, setAllRaces] = useState<RaceEvent[]>([]);
    const [plannedEvents, setPlannedEvents] = useState<ExternalEvent[]>([]);
    const [favoriteGroupRides, setFavoriteGroupRides] = useState<FavoriteGroupRide[]>([]);
    const [favoriteRaces, setFavoriteRaces] = useState<FavoriteRace[]>([]);
    const [customRaceIds, setCustomRaceIds] = useState<Set<string>>(new Set());
    const [participatedRaceIds, setParticipatedRaceIds] = useState<Set<string>>(new Set());
    const [isLoading, setIsLoading] = useState(true);

    // External Search State (Outside API)
    const [externalEvents, setExternalEvents] = useState<ExternalEvent[]>([]);
    const [isSearchingExternal, setIsSearchingExternal] = useState(false);
    const [processingEventId, setProcessingEventId] = useState<string | null>(null);

    // Modals
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editingRace, setEditingRace] = useState<RaceEvent | undefined>(undefined);
    const [isSelectionOpen, setIsSelectionOpen] = useState(false);
    const [selectedRace, setSelectedRace] = useState<RaceEvent | null>(null);

    // Synchronize tab state with search params and sessionStorage
    const handleTabChange = (tab: 'find' | 'my') => {
        setActiveTab(tab);
        sessionStorage.setItem(STORAGE_TAB_KEY, tab);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('tab', tab);
            return next;
        }, { replace: true });
    };

    // Synchronize event filter with search params and sessionStorage
    const handleFilterChange = (filter: 'all' | 'race' | 'group_ride') => {
        setEventTypeFilter(filter);
        sessionStorage.setItem(STORAGE_FILTER_KEY, filter);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (filter === 'all') next.delete('filter');
            else next.set('filter', filter);
            return next;
        }, { replace: true });
    };

    // Synchronize search input with search params and sessionStorage
    const handleSearchChange = (term: string) => {
        setSearchTerm(term);
        if (term) {
            sessionStorage.setItem(STORAGE_SEARCH_KEY, term);
        } else {
            sessionStorage.removeItem(STORAGE_SEARCH_KEY);
        }
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (!term) next.delete('q');
            else next.set('q', term);
            return next;
        }, { replace: true });
    };

    // Save scroll position whenever scrolling
    useEffect(() => {
        const onScroll = () => {
            sessionStorage.setItem(STORAGE_SCROLL_KEY, window.scrollY.toString());
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, []);

    // Record scroll position before navigating via click
    const recordScroll = () => {
        sessionStorage.setItem(STORAGE_SCROLL_KEY, window.scrollY.toString());
    };

    // Restore scroll position after events finish loading
    const hasRestoredScroll = React.useRef(false);
    useEffect(() => {
        if (!isLoading && !hasRestoredScroll.current) {
            const savedScroll = sessionStorage.getItem(STORAGE_SCROLL_KEY);
            if (savedScroll) {
                const scrollY = parseInt(savedScroll, 10);
                if (!isNaN(scrollY) && scrollY > 0) {
                    hasRestoredScroll.current = true;
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            window.scrollTo({ top: scrollY, behavior: 'instant' });
                        });
                    });
                }
            }
        }
    }, [isLoading]);

    // Debounce search term (350ms)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 350);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // 1. Fetch All Events & User Preferences
    const fetchData = async () => {
        setIsLoading(true);
        try {
            // Fetch internal community events
            const races = await raceEventService.getAllRaceEvents();
            setAllRaces(races);

            const standardIds = new Set(['emerald-city-classic', 'cascade-climbfest', 'puget-sound-gp', 'rainier-road-race', 'giro-de-san-francisco']);
            const custom = new Set(races.filter(r => !standardIds.has(r.id)).map(r => r.id));
            setCustomRaceIds(custom);

            // Fetch user profile for planned events & favorites
            if (currentUser) {
                const profile = await userService.getUserProfile(currentUser.uid);
                if (profile) {
                    if (profile.plannedEvents) setPlannedEvents(profile.plannedEvents);
                    if (profile.favoriteGroupRides) setFavoriteGroupRides(profile.favoriteGroupRides);
                    if (profile.favoriteRaces) setFavoriteRaces(profile.favoriteRaces);
                }

                // Check participation in past races
                const todayDate = new Date();
                todayDate.setHours(0, 0, 0, 0);
                const pastRaces = races.filter(r => new Date(r.date) < todayDate && (r.ownerId !== currentUser.uid || !!r.seriesId));
                const myRaceIds = new Set<string>();

                await Promise.all(pastRaces.map(async (race) => {
                    try {
                        const catsSnapshot = await getDocs(collection(db, 'races', race.id, 'categories'));
                        let userInRace = false;
                        catsSnapshot.forEach(doc => {
                            const data = doc.data();
                            const athletes = data.athletes || [];
                            if (athletes.some((a: any) => a.userId === currentUser.uid || a.id === currentUser.uid)) {
                                userInRace = true;
                            }
                        });
                        if (userInRace) {
                            myRaceIds.add(race.id);
                        }
                    } catch (err) {
                        console.error(`Failed to check participation for race ${race.id}`, err);
                    }
                }));

                setParticipatedRaceIds(myRaceIds);
            }
        } catch (e) {
            console.error("Failed to load races", e);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [currentUser]);

    // 2. External Search (Outside API / BikeReg)
    useEffect(() => {
        if (!debouncedSearchTerm.trim()) {
            setExternalEvents([]);
            return;
        }

        const fetchExternal = async () => {
            setIsSearchingExternal(true);
            try {
                const results = await eventIntegrationService.searchEvents(debouncedSearchTerm);
                setExternalEvents(results || []);
            } catch (error) {
                console.error("External search failed", error);
            } finally {
                setIsSearchingExternal(false);
            }
        };

        fetchExternal();
    }, [debouncedSearchTerm]);

    // Favorite Sets for O(1) Lookup
    const savedGroupRideIds = useMemo(() => new Set(favoriteGroupRides.map(r => r.id)), [favoriteGroupRides]);
    const savedRaceIds = useMemo(() => new Set(favoriteRaces.map(r => r.id)), [favoriteRaces]);
    const plannedEventIds = useMemo(() => new Set(plannedEvents.map(e => e.id)), [plannedEvents]);

    // --- Filtered Community Events for Catalog Search ---
    const catalogEvents = useMemo(() => {
        // Exclude per-date occurrences from catalog list (they inherit from series)
        let result = allRaces.filter(r => !r.seriesId);

        if (eventTypeFilter === 'race') {
            result = result.filter(r => r.eventType !== 'group_ride');
        } else if (eventTypeFilter === 'group_ride') {
            result = result.filter(r => r.eventType === 'group_ride');
        }

        if (debouncedSearchTerm.trim()) {
            const lowerTerm = debouncedSearchTerm.toLowerCase();
            result = result.filter(r =>
                r.name.toLowerCase().includes(lowerTerm) ||
                r.groupRideDetails?.locationName?.toLowerCase().includes(lowerTerm)
            );
        }

        return result;
    }, [allRaces, debouncedSearchTerm, eventTypeFilter]);

    // --- My Events Derived Data ---
    const upcomingPlannedEvents = useMemo(() => {
        const todayMidnight = new Date();
        todayMidnight.setHours(0, 0, 0, 0);
        return plannedEvents
            .filter(e => new Date(e.date) >= todayMidnight)
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }, [plannedEvents]);

    const myPastRaces = useMemo(() => {
        if (!currentUser) return [];
        const todayMidnight = new Date();
        todayMidnight.setHours(0, 0, 0, 0);

        return allRaces
            .filter(race => {
                const isOwner = race.ownerId === currentUser.uid && !race.seriesId;
                const isMyRace = isOwner || participatedRaceIds.has(race.id) ||
                    (race.categories || []).some(cat =>
                        (cat.athletes || []).some(a => a.userId === currentUser.uid || a.id === currentUser.uid)
                    );
                const isPast = new Date(race.date) < todayMidnight;
                return isMyRace && isPast;
            })
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [allRaces, currentUser, participatedRaceIds]);

    const totalMyEventsCount = upcomingPlannedEvents.length + favoriteGroupRides.length + favoriteRaces.length + myPastRaces.length;

    // --- Favorite Handlers ---
    const handleToggleFavorite = async (race: RaceEvent) => {
        if (!currentUser) {
            navigate(`/auth?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`);
            return;
        }

        const isGroupRide = race.eventType === 'group_ride';
        const raceId = race.slug || race.id;

        if (isGroupRide) {
            const isAlreadyFavorited = savedGroupRideIds.has(raceId);
            if (isAlreadyFavorited) {
                // Optimistic remove
                setFavoriteGroupRides(prev => prev.filter(r => r.id !== raceId));
                try {
                    await userService.removeFavoriteGroupRide(currentUser.uid, raceId);
                } catch (e) {
                    console.error("Failed to remove favorite group ride", e);
                    fetchData();
                }
            } else {
                // Optimistic add
                const newRide: FavoriteGroupRide = {
                    id: raceId,
                    name: race.name,
                    locationName: race.groupRideDetails?.locationName,
                    savedAt: Date.now()
                };
                setFavoriteGroupRides(prev => [newRide, ...prev]);
                try {
                    await userService.addFavoriteGroupRide(currentUser.uid, newRide);
                } catch (e) {
                    console.error("Failed to add favorite group ride", e);
                    fetchData();
                }
            }
        } else {
            // Community race favorite
            const isAlreadyFavorited = savedRaceIds.has(raceId);
            if (isAlreadyFavorited) {
                // Optimistic remove
                setFavoriteRaces(prev => prev.filter(r => r.id !== raceId));
                try {
                    await userService.removeFavoriteRace(currentUser.uid, raceId);
                } catch (e) {
                    console.error("Failed to remove favorite race", e);
                    fetchData();
                }
            } else {
                // Optimistic add
                const newRace: FavoriteRace = {
                    id: raceId,
                    name: race.name,
                    date: race.date,
                    savedAt: Date.now()
                };
                setFavoriteRaces(prev => [newRace, ...prev]);
                try {
                    await userService.addFavoriteRace(currentUser.uid, newRace);
                } catch (e) {
                    console.error("Failed to add favorite race", e);
                    fetchData();
                }
            }
        }
    };

    const handlePlanExternalEvent = async (event: ExternalEvent) => {
        if (!currentUser) {
            navigate(`/auth?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`);
            return;
        }
        setProcessingEventId(event.id);
        try {
            await userService.addPlannedEvent(currentUser.uid, event);
            setPlannedEvents(prev => [...prev, event]);
        } catch (e) {
            alert("Failed to add to calendar.");
        } finally {
            setProcessingEventId(null);
        }
    };

    const handleRemovePlannedEvent = async (event: ExternalEvent) => {
        if (!currentUser) return;
        if (!confirm(`Remove ${event.name} from your calendar?`)) return;
        setProcessingEventId(event.id);
        try {
            await userService.removePlannedEvent(currentUser.uid, event);
            setPlannedEvents(prev => prev.filter(e => e.id !== event.id));
        } catch (e) {
            alert("Failed to remove event.");
        } finally {
            setProcessingEventId(null);
        }
    };

    const handleRemoveFavoriteGroupRide = async (rideId: string) => {
        if (!currentUser) return;
        setFavoriteGroupRides(prev => prev.filter(r => r.id !== rideId));
        try {
            await userService.removeFavoriteGroupRide(currentUser.uid, rideId);
        } catch (e) {
            alert('Failed to remove saved ride.');
            fetchData();
        }
    };

    const handleRemoveFavoriteRace = async (raceId: string) => {
        if (!currentUser) return;
        setFavoriteRaces(prev => prev.filter(r => r.id !== raceId));
        try {
            await userService.removeFavoriteRace(currentUser.uid, raceId);
        } catch (e) {
            alert('Failed to remove saved race.');
            fetchData();
        }
    };

    // Category Selection Modal Trigger
    const handleSelectGroup = (race: RaceEvent) => {
        setSelectedRace(race);
        setIsSelectionOpen(true);
    };

    // Admin Handlers
    const handleOpenCreateModal = () => {
        setEditingRace(undefined);
        setIsEditorOpen(true);
    };

    const handleOpenEditModal = async (raceToEdit: RaceEvent) => {
        setIsLoading(true);
        try {
            const fullRace = await raceEventService.getRaceEventBySlug(raceToEdit.id);
            if (fullRace) {
                setEditingRace(fullRace);
                setIsEditorOpen(true);
            } else {
                alert("Could not load event details.");
            }
        } catch (error) {
            console.error("Error loading race details:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveRace = async (savedRace: RaceEvent) => {
        try {
            await raceEventService.saveRaceEvent(savedRace);
            await fetchData();
            setIsEditorOpen(false);
            setEditingRace(undefined);
        } catch (error) {
            alert("Could not save the race.");
        }
    };

    const handleDeleteEvent = async (raceToDelete: RaceEvent) => {
        try {
            await raceEventService.deleteRaceEvent(raceToDelete.id);
            await fetchData();
            setIsEditorOpen(false);
            setEditingRace(undefined);
        } catch (error) {
            alert("Could not delete the event.");
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 text-white pb-28" onClickCapture={recordScroll}>
            <SEO title="Races & Events | RaceLineIQ" />

            {/* Sticky Header with Title & Segmented Switcher */}
            <div className="sticky top-0 z-30 bg-gray-900/95 backdrop-blur-md border-b border-gray-800 px-4 pt-4 pb-3">
                <div className="flex justify-between items-center mb-3">
                    <h1 className="text-2xl font-bold text-white tracking-tight">
                        Events & Races
                    </h1>
                    {isAdmin && (
                        <button
                            onClick={handleOpenCreateModal}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500 text-gray-950 font-bold rounded-lg text-xs shadow-md shadow-cyan-500/20 active:scale-95 transition-transform"
                        >
                            <PlusIcon className="w-4 h-4" />
                            Add Event
                        </button>
                    )}
                </div>

                {/* Segmented Pill Control */}
                <div className="flex bg-gray-800/80 p-1 rounded-xl border border-gray-700/80">
                    <button
                        onClick={() => handleTabChange('find')}
                        className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                            activeTab === 'find'
                                ? 'bg-cyan-500 text-gray-950 shadow-sm'
                                : 'text-gray-400 hover:text-white'
                        }`}
                    >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                        Find Events
                    </button>
                    <button
                        onClick={() => handleTabChange('my')}
                        className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                            activeTab === 'my'
                                ? 'bg-cyan-500 text-gray-950 shadow-sm'
                                : 'text-gray-400 hover:text-white'
                        }`}
                    >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        My Events
                        {totalMyEventsCount > 0 && (
                            <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                                activeTab === 'my' ? 'bg-gray-950 text-cyan-300' : 'bg-gray-700 text-gray-300'
                            }`}>
                                {totalMyEventsCount}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* TAB CONTENT */}
            <div className="p-4 space-y-6">
                {activeTab === 'find' ? (
                    /* FIND EVENTS TAB */
                    <div className="space-y-4">
                        {/* Search Input Bar */}
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                placeholder="Search races, group rides, locations..."
                                className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-10 pr-10 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all shadow-inner"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => handleSearchChange('')}
                                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-white"
                                    aria-label="Clear search"
                                >
                                    <span className="text-sm font-bold bg-gray-700 rounded-full w-5 h-5 flex items-center justify-center">✕</span>
                                </button>
                            )}
                        </div>

                        {/* Filter Chips */}
                        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
                            <button
                                onClick={() => handleFilterChange('all')}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors border ${
                                    eventTypeFilter === 'all'
                                        ? 'bg-cyan-500 text-gray-950 border-cyan-400 shadow-sm'
                                        : 'bg-gray-800 text-gray-300 border-gray-700 hover:border-gray-500'
                                }`}
                            >
                                All Events
                            </button>
                            <button
                                onClick={() => handleFilterChange('race')}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors border flex items-center gap-1 ${
                                    eventTypeFilter === 'race'
                                        ? 'bg-cyan-500 text-gray-950 border-cyan-400 shadow-sm'
                                        : 'bg-gray-800 text-gray-300 border-gray-700 hover:border-gray-500'
                                }`}
                            >
                                <span>🏁</span> Races
                            </button>
                            <button
                                onClick={() => handleFilterChange('group_ride')}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors border flex items-center gap-1 ${
                                    eventTypeFilter === 'group_ride'
                                        ? 'bg-cyan-500 text-gray-950 border-cyan-400 shadow-sm'
                                        : 'bg-gray-800 text-gray-300 border-gray-700 hover:border-gray-500'
                                }`}
                            >
                                <span>🚴</span> Group Rides
                            </button>
                        </div>

                        {/* Official Calendar Results (Outside API) when searching */}
                        {(isSearchingExternal || externalEvents.length > 0) && (
                            <section className="space-y-3 pt-2">
                                <div className="flex items-center justify-between border-b border-gray-800 pb-2">
                                    <h2 className="text-sm font-bold text-white flex items-center gap-1.5">
                                        <span className="text-cyan-400">Official Calendar</span> Results
                                        {isSearchingExternal && (
                                            <svg className="animate-spin h-3.5 w-3.5 text-cyan-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        )}
                                    </h2>
                                    <span className="text-[10px] text-gray-500">BikeReg / Outside</span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {externalEvents.map(event => (
                                        <ExternalEventCard
                                            key={event.id}
                                            event={event}
                                            onPlan={handlePlanExternalEvent}
                                            onCreate={() => {}}
                                            isPlanned={plannedEventIds.has(event.id)}
                                            isProcessing={processingEventId === event.id}
                                            showCreate={false}
                                        />
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Community Catalog Results */}
                        <section className="space-y-3 pt-2">
                            <div className="flex items-center justify-between border-b border-gray-800 pb-2">
                                <h2 className="text-sm font-bold text-gray-300">
                                    {searchTerm ? `Matching Events (${catalogEvents.length})` : `Browse Catalog (${catalogEvents.length})`}
                                </h2>
                            </div>

                            {isLoading ? (
                                <div className="flex justify-center items-center py-16">
                                    <svg className="animate-spin h-8 w-8 text-cyan-400" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                </div>
                            ) : catalogEvents.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {catalogEvents.map(race => {
                                        const raceId = race.slug || race.id;
                                        const isFav = race.eventType === 'group_ride'
                                            ? savedGroupRideIds.has(raceId)
                                            : savedRaceIds.has(raceId);

                                        return (
                                            <div key={race.id} className="h-full">
                                                <RaceCard
                                                    race={race}
                                                    isCustom={customRaceIds.has(race.id)}
                                                    currentUserId={currentUser?.uid}
                                                    isAdmin={isAdmin}
                                                    onEdit={handleOpenEditModal}
                                                    onSelectGroup={handleSelectGroup}
                                                    isFavorited={isFav}
                                                    onToggleFavorite={handleToggleFavorite}
                                                    linkState={{ from: 'find' }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-12 px-4 bg-gray-800/40 rounded-2xl border border-dashed border-gray-700">
                                    <p className="text-gray-400 text-sm">
                                        {searchTerm ? `No events found matching "${searchTerm}".` : "No events available in the catalog."}
                                    </p>
                                    {searchTerm && (
                                        <button
                                            onClick={() => handleSearchChange('')}
                                            className="mt-3 text-cyan-400 text-xs font-semibold underline hover:text-cyan-300"
                                        >
                                            Clear Search
                                        </button>
                                    )}
                                </div>
                            )}
                        </section>
                    </div>
                ) : (
                    /* MY EVENTS TAB */
                    <div className="space-y-8">
                        {/* 1. Planned Races */}
                        <section className="space-y-3">
                            <h2 className="text-sm font-bold text-cyan-400 uppercase tracking-wider flex items-center justify-between border-b border-gray-800 pb-2">
                                <span>📅 Planned Races</span>
                                <span className="text-xs text-gray-500 font-normal">{upcomingPlannedEvents.length} upcoming</span>
                            </h2>

                            {upcomingPlannedEvents.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {upcomingPlannedEvents.map(event => (
                                        <ExternalEventCard
                                            key={event.id}
                                            event={event}
                                            onPlan={() => handleRemovePlannedEvent(event)}
                                            onCreate={() => {}}
                                            isPlanned={true}
                                            isProcessing={processingEventId === event.id}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="bg-gray-800/40 rounded-xl p-5 text-center border border-gray-700/60 border-dashed text-xs text-gray-400">
                                    No upcoming races planned.
                                </div>
                            )}
                        </section>

                        {/* 2. Saved Group Rides */}
                        <section className="space-y-3">
                            <h2 className="text-sm font-bold text-cyan-400 uppercase tracking-wider flex items-center justify-between border-b border-gray-800 pb-2">
                                <span>⭐ Saved Group Rides</span>
                                <span className="text-xs text-gray-500 font-normal">{favoriteGroupRides.length} saved</span>
                            </h2>

                            {favoriteGroupRides.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {favoriteGroupRides
                                        .slice()
                                        .sort((a, b) => b.savedAt - a.savedAt)
                                        .map(ride => (
                                            <div
                                                key={ride.id}
                                                className="bg-gray-800 border border-gray-700 rounded-xl p-4 shadow-md flex flex-col justify-between gap-3"
                                            >
                                                <div>
                                                    <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">
                                                        ⭐ Saved Group Ride
                                                    </span>
                                                    <h3 className="font-bold text-white text-base mt-1">{ride.name}</h3>
                                                    {ride.locationName && (
                                                        <p className="text-gray-400 text-xs mt-1">📍 {ride.locationName}</p>
                                                    )}
                                                </div>
                                                <div className="flex gap-2 pt-2 border-t border-gray-700/60">
                                                    <Link
                                                        to={`/ride/${ride.id}`}
                                                        state={{ from: 'my' }}
                                                        className="flex-1 text-center py-2 bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 text-xs font-bold rounded-lg border border-cyan-500/40 transition-colors"
                                                    >
                                                        View Ride
                                                    </Link>
                                                    <button
                                                        onClick={() => handleRemoveFavoriteGroupRide(ride.id)}
                                                        className="px-3 py-2 bg-gray-700 hover:bg-red-900/30 text-gray-400 hover:text-red-400 text-xs font-bold rounded-lg border border-gray-600 transition-colors"
                                                        title="Remove from saved rides"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            ) : (
                                <div className="bg-gray-800/40 rounded-xl p-5 text-center border border-gray-700/60 border-dashed text-xs text-gray-400">
                                    You haven't saved any group rides yet.
                                </div>
                            )}
                        </section>

                        {/* 3. Saved Races */}
                        <section className="space-y-3">
                            <h2 className="text-sm font-bold text-cyan-400 uppercase tracking-wider flex items-center justify-between border-b border-gray-800 pb-2">
                                <span>🏁 Saved Races</span>
                                <span className="text-xs text-gray-500 font-normal">{favoriteRaces.length} saved</span>
                            </h2>

                            {favoriteRaces.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {favoriteRaces
                                        .slice()
                                        .sort((a, b) => b.savedAt - a.savedAt)
                                        .map(favRace => {
                                            const fullEvent = allRaces.find(r => r.id === favRace.id || r.slug === favRace.id);
                                            return (
                                                <div
                                                    key={favRace.id}
                                                    className="bg-gray-800 border border-gray-700 rounded-xl p-4 shadow-md flex flex-col justify-between gap-3"
                                                >
                                                    <div>
                                                        <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider">
                                                            🏁 Saved Race
                                                        </span>
                                                        <h3 className="font-bold text-white text-base mt-1">{favRace.name}</h3>
                                                        <p className="text-gray-400 text-xs mt-1">
                                                            📅 {new Date(favRace.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                                        </p>
                                                    </div>
                                                    <div className="flex gap-2 pt-2 border-t border-gray-700/60">
                                                        <button
                                                            onClick={() => {
                                                                if (fullEvent) {
                                                                    const catCount = fullEvent.categoryCount !== undefined ? fullEvent.categoryCount : (fullEvent.categories?.length || 0);
                                                                    if (catCount > 1) {
                                                                        handleSelectGroup(fullEvent);
                                                                    } else {
                                                                        const catId = fullEvent.categories?.[0]?.slug || fullEvent.categories?.[0]?.id;
                                                                        navigate(catId ? `/race/${fullEvent.slug || fullEvent.id}/category/${catId}` : `/race/${fullEvent.slug || fullEvent.id}`);
                                                                    }
                                                                } else {
                                                                    navigate(`/race/${favRace.id}`);
                                                                }
                                                            }}
                                                            className="flex-1 text-center py-2 bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 text-xs font-bold rounded-lg border border-cyan-500/40 transition-colors"
                                                        >
                                                            View Race
                                                        </button>
                                                        <button
                                                            onClick={() => handleRemoveFavoriteRace(favRace.id)}
                                                            className="px-3 py-2 bg-gray-700 hover:bg-red-900/30 text-gray-400 hover:text-red-400 text-xs font-bold rounded-lg border border-gray-600 transition-colors"
                                                            title="Remove from saved races"
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    }
                                </div>
                            ) : (
                                <div className="bg-gray-800/40 rounded-xl p-5 text-center border border-gray-700/60 border-dashed text-xs text-gray-400">
                                    You haven't saved any races yet.
                                </div>
                            )}
                        </section>

                        {/* 4. Past Races (History) */}
                        <section className="space-y-3">
                            <h2 className="text-sm font-bold text-cyan-400 uppercase tracking-wider flex items-center justify-between border-b border-gray-800 pb-2">
                                <span>⏱️ Past Races</span>
                                <span className="text-xs text-gray-500 font-normal">{myPastRaces.length} races</span>
                            </h2>

                            {myPastRaces.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {myPastRaces.map(race => (
                                        <div key={race.id} className="h-full">
                                            <RaceCard
                                                race={race}
                                                isCustom={customRaceIds.has(race.id)}
                                                currentUserId={currentUser?.uid}
                                                isAdmin={isAdmin}
                                                onEdit={handleOpenEditModal}
                                                onSelectGroup={handleSelectGroup}
                                                isFavorited={savedRaceIds.has(race.slug || race.id)}
                                                onToggleFavorite={handleToggleFavorite}
                                                linkState={{ from: 'my' }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="bg-gray-800/40 rounded-xl p-5 text-center border border-gray-700/60 border-dashed text-xs text-gray-400">
                                    Races you have participated in or created will appear here after they occur.
                                </div>
                            )}
                        </section>

                        {/* Bottom CTA to Find Events if list is thin */}
                        <div className="pt-4 text-center">
                            <button
                                onClick={() => handleTabChange('find')}
                                className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold rounded-xl text-xs shadow-lg shadow-cyan-500/20 active:scale-95 transition-all"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                Find More Events in Catalog
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Category Selection Modal */}
            {isSelectionOpen && selectedRace && (
                <CategorySelectionModal
                    isOpen={isSelectionOpen}
                    onClose={() => setIsSelectionOpen(false)}
                    race={selectedRace}
                />
            )}

            {/* Admin Race Editor Modal */}
            {isEditorOpen && (
                <RaceEditorModal
                    onSave={handleSaveRace}
                    onClose={() => {
                        setIsEditorOpen(false);
                        setEditingRace(undefined);
                    }}
                    onDelete={handleDeleteEvent}
                    eventToEdit={editingRace}
                />
            )}
        </div>
    );
};

export default MobileRacesPage;
